import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient, { VideoInsights } from '../api/client';
import { ArrowLeft, Download, Shield, Clock, Hash, FileBox, MessageSquare, Sparkles, X, Send, Loader2, Users, Eye, Car, FileText, ChevronRight, ChevronDown, ChevronUp, Tag, AlertTriangle, History } from 'lucide-react';
import { format } from 'date-fns';
import { useState, useEffect, useRef } from 'react';

type PanelMode = 'none' | 'aegis' | 'insights';

export default function MediaViewer() {
  const { evidenceId } = useParams<{ evidenceId: string }>();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [panelMode, setPanelMode] = useState<PanelMode>('none');
  const [aegisMessages, setAegisMessages] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const [aegisInput, setAegisInput] = useState('');
  const [aegisLoading, setAegisLoading] = useState(false);
  const [insights, setInsights] = useState<VideoInsights | null>(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [showCustody, setShowCustody] = useState(false);

  const { data: evidence, isLoading } = useQuery({
    queryKey: ['evidence', evidenceId],
    queryFn: () => apiClient.getEvidence(evidenceId!),
    enabled: !!evidenceId,
  });

  const { data: chain, isLoading: chainLoading, refetch: fetchChain } = useQuery({
    queryKey: ['custody', evidenceId],
    queryFn: () => apiClient.getEvidenceCustodyChain(evidenceId!),
    enabled: false,
  });

  // Log view event
  useEffect(() => {
    if (evidenceId) {
      apiClient.logViewEvent(evidenceId).catch(() => {});
    }
  }, [evidenceId]);

  // Load preview
  useEffect(() => {
    if (!evidence) return;
    if (evidence.externalUrl) {
      setPreviewUrl(evidence.externalUrl);
    } else if (evidenceId) {
      apiClient.getPreviewBlobUrl(evidenceId).then(setPreviewUrl).catch(() => {});
    }
  }, [evidence, evidenceId]);

  // Load insights when panel opens
  useEffect(() => {
    if (panelMode === 'insights' && evidenceId && !insights) {
      setInsightsLoading(true);
      apiClient.getVideoInsights(evidenceId)
        .then(setInsights)
        .finally(() => setInsightsLoading(false));
    }
  }, [panelMode, evidenceId, insights]);

  const handleAegisSubmit = async () => {
    if (!aegisInput.trim() || !evidence || aegisLoading) return;
    const question = aegisInput.trim();
    setAegisInput('');
    setAegisMessages(prev => [...prev, { role: 'user', content: question }]);
    setAegisLoading(true);
    try {
      const { answer } = await apiClient.aegisAsk(evidence.id, question, evidence.caseNumber);
      setAegisMessages(prev => [...prev, { role: 'assistant', content: answer }]);
    } catch {
      setAegisMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I encountered an error processing your question.' }]);
    } finally {
      setAegisLoading(false);
    }
  };

  if (isLoading) return <div className="text-slate-400">Loading...</div>;
  if (!evidence) return <div className="text-slate-400">Evidence not found</div>;

  const isVideo = evidence.fileType?.startsWith('video/');
  const isAudio = evidence.fileType?.startsWith('audio/');
  const isImage = evidence.fileType?.startsWith('image/');

  const handleDownload = async () => {
    if (!evidenceId) return;
    try {
      await apiClient.logDownloadEvent(evidenceId);
      const { url } = await apiClient.downloadEvidence(evidenceId);
      window.open(url, '_blank');
    } catch { /* ignore */ }
  };

  return (
    <div className="flex gap-4">
      {/* Main Content */}
      <div className={`flex-1 space-y-6 transition-all ${panelMode !== 'none' ? 'mr-0' : ''}`}>
        <Link to={`/cases/${evidence.caseNumber}`} className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-white transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to case
        </Link>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-bold text-white">{evidence.title || evidence.fileName}</h1>
            <p className="text-sm text-slate-400 mt-1">{evidence.description}</p>
          </div>
          <div className="flex items-center gap-2">
            {/* AI Agent Buttons */}
            <button
              onClick={() => setPanelMode(panelMode === 'aegis' ? 'none' : 'aegis')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                panelMode === 'aegis'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-rtcc-panel border border-slate-700 text-slate-300 hover:border-emerald-500 hover:text-emerald-400'
              }`}
            >
              <MessageSquare className="h-4 w-4" />
              Agent Aegis
            </button>
            <button
              onClick={() => setPanelMode(panelMode === 'insights' ? 'none' : 'insights')}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                panelMode === 'insights'
                  ? 'bg-violet-600 text-white'
                  : 'bg-rtcc-panel border border-slate-700 text-slate-300 hover:border-violet-500 hover:text-violet-400'
              }`}
            >
              <Sparkles className="h-4 w-4" />
              Insights
            </button>
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-3 py-2 bg-rtcc-panel border border-slate-700 rounded-lg text-sm text-white hover:border-slate-600 transition-colors"
            >
              <Download className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Media Preview */}
        <div className="bg-rtcc-panel rounded-xl border border-slate-700 overflow-hidden">
          {previewUrl ? (
            isVideo ? (
              <video src={previewUrl} controls className="w-full max-h-[60vh]" />
            ) : isAudio ? (
              <div className="p-8">
                <audio src={previewUrl} controls className="w-full" />
              </div>
            ) : isImage ? (
              <img src={previewUrl} alt={evidence.fileName} className="w-full max-h-[60vh] object-contain" />
            ) : (
              <div className="p-12 text-center">
                <FileBox className="h-16 w-16 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400">Preview not available for this file type</p>
                <p className="text-slate-500 text-sm mt-1">{evidence.fileType}</p>
              </div>
            )
          ) : (
            <div className="p-12 text-center text-slate-500">Loading preview...</div>
          )}
        </div>

        {/* Metadata */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <MetaCard icon={<Hash className="h-4 w-4" />} label="SHA-256 Hash" value={evidence.sha256Hash} mono />
          <MetaCard icon={<Shield className="h-4 w-4" />} label="Integrity" value={evidence.integrityStatus} />
          <MetaCard icon={<Clock className="h-4 w-4" />} label="Uploaded" value={format(new Date(evidence.uploadedAt), 'MMM d, yyyy h:mm a')} />
          <MetaCard icon={<FileBox className="h-4 w-4" />} label="Type" value={`${evidence.evidenceType} — ${evidence.fileType}`} />
        </div>

        {/* Custody Chain — on-demand */}
        <div>
          <button
            onClick={() => {
              if (!chain && !chainLoading) fetchChain();
              setShowCustody((v) => !v);
            }}
            className="flex items-center gap-2 w-full px-4 py-3 bg-rtcc-panel rounded-lg border border-slate-700 text-sm font-medium text-slate-300 hover:border-slate-500 hover:text-white transition-colors"
          >
            <History className="h-4 w-4" />
            <span>Chain of Custody{chain ? ` (${chain.length})` : ''}</span>
            <span className="ml-auto">
              {chainLoading ? (
                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              ) : showCustody ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </span>
          </button>

          {showCustody && chain && chain.length > 0 && (
            <div className="mt-2 space-y-2">
              {chain.map((event) => (
                <div key={event.id} className="flex items-start gap-3 p-3 bg-rtcc-panel rounded-lg border border-slate-700">
                  <div className="h-2 w-2 mt-2 rounded-full bg-rtcc-accent flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-white font-medium">{event.performedByName || event.performedBy}</span>
                      <span className="text-xs px-2 py-0.5 bg-slate-700 text-slate-300 rounded-full">{event.eventType}</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">{event.description}</p>
                    <p className="text-[10px] text-slate-500 mt-1">
                      {format(new Date(event.performedAt), 'MMM d, yyyy h:mm:ss a')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showCustody && chain && chain.length === 0 && (
            <p className="mt-2 text-sm text-slate-500 px-4">No custody events recorded.</p>
          )}
        </div>
      </div>

      {/* Right Panel - Agent Aegis or Insights */}
      {panelMode !== 'none' && (
        <div className="w-96 flex-shrink-0">
          {panelMode === 'aegis' && (
            <AgentAegisPanel
              messages={aegisMessages}
              input={aegisInput}
              loading={aegisLoading}
              onInputChange={setAegisInput}
              onSubmit={handleAegisSubmit}
              onClose={() => setPanelMode('none')}
            />
          )}
          {panelMode === 'insights' && (
            <VideoInsightsPanel
              insights={insights}
              loading={insightsLoading}
              onClose={() => setPanelMode('none')}
            />
          )}
        </div>
      )}
    </div>
  );
}

function MetaCard({ icon, label, value, mono }: { icon: React.ReactNode; label: string; value: string; mono?: boolean }) {
  return (
    <div className="p-3 bg-rtcc-panel rounded-lg border border-slate-700">
      <div className="flex items-center gap-2 text-slate-400 mb-1">
        {icon}
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-sm text-white truncate ${mono ? 'font-mono text-xs' : ''}`}>{value}</p>
    </div>
  );
}

// ─── Agent Aegis Panel ────────────────────────────────────────────────────────

function AgentAegisPanel({ messages, input, loading, onInputChange, onSubmit, onClose }: {
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  input: string;
  loading: boolean;
  onInputChange: (v: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col bg-rtcc-panel rounded-xl border border-slate-700 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-gradient-to-r from-emerald-700 to-teal-600">
        <div className="flex items-center gap-2 text-white">
          <Shield className="h-4 w-4" />
          <span className="text-sm font-semibold">Agent Aegis</span>
          <span className="text-xs text-emerald-200">Evidence Intelligence</span>
        </div>
        <button onClick={onClose} className="text-emerald-200 hover:text-white p-1">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <Shield className="h-10 w-10 text-emerald-500/30 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Ask me anything about this evidence</p>
            <p className="text-slate-500 text-xs mt-1">I can analyze content, find details, and answer questions</p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-lg px-4 py-2.5 ${
              msg.role === 'user' 
                ? 'bg-emerald-600 text-white' 
                : 'bg-slate-700 text-slate-200'
            }`}>
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-slate-700 rounded-lg px-4 py-2.5 flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-emerald-400" />
              <span className="text-sm text-slate-400">Analyzing...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-slate-700">
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about this evidence..."
            className="flex-1 px-3 py-2 bg-rtcc-dark border border-slate-600 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
          <button
            onClick={onSubmit}
            disabled={loading || !input.trim()}
            className="px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 disabled:opacity-50 transition-colors"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Video Insights Panel ─────────────────────────────────────────────────────

function VideoInsightsPanel({ insights, loading, onClose }: {
  insights: VideoInsights | null;
  loading: boolean;
  onClose: () => void;
}) {
  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col bg-rtcc-panel rounded-xl border border-slate-700 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-gradient-to-r from-violet-700 to-purple-600">
        <div className="flex items-center gap-2 text-white">
          <Sparkles className="h-4 w-4" />
          <span className="text-sm font-semibold">Video Insights</span>
          <span className="text-xs text-violet-200">AI Extraction</span>
        </div>
        <button onClick={onClose} className="text-violet-200 hover:text-white p-1">
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-violet-400 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">Extracting insights...</p>
          </div>
        ) : !insights ? (
          <div className="text-center py-8">
            <Sparkles className="h-10 w-10 text-violet-500/30 mx-auto mb-3" />
            <p className="text-slate-400 text-sm">No insights available</p>
            <p className="text-slate-500 text-xs mt-1">This file hasn't been processed yet</p>
          </div>
        ) : (
          <>
            {/* Duration & Stats */}
            {(insights.duration || insights.scenes || insights.shots) && (
              <InsightSection title="Overview" icon={<Eye className="h-4 w-4" />}>
                <div className="grid grid-cols-3 gap-2 text-center">
                  {insights.duration && (
                    <div className="bg-slate-700/50 rounded-lg p-2">
                      <p className="text-white font-semibold text-sm">{insights.duration}</p>
                      <p className="text-slate-400 text-xs">Duration</p>
                    </div>
                  )}
                  {insights.scenes && (
                    <div className="bg-slate-700/50 rounded-lg p-2">
                      <p className="text-white font-semibold text-sm">{insights.scenes}</p>
                      <p className="text-slate-400 text-xs">Scenes</p>
                    </div>
                  )}
                  {insights.shots && (
                    <div className="bg-slate-700/50 rounded-lg p-2">
                      <p className="text-white font-semibold text-sm">{insights.shots}</p>
                      <p className="text-slate-400 text-xs">Shots</p>
                    </div>
                  )}
                </div>
              </InsightSection>
            )}

            {/* People Detected */}
            {insights.observedPeople && insights.observedPeople.length > 0 && (
              <InsightSection title={`People Detected (${insights.observedPeople.length})`} icon={<Users className="h-4 w-4" />}>
                <div className="space-y-2">
                  {insights.observedPeople.slice(0, 5).map((person, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-700/50 rounded-lg px-3 py-2">
                      <span className="text-sm text-white">Person #{person.id}</span>
                      {person.instances?.[0] && (
                        <span className="text-xs text-slate-400">{person.instances[0].adjustedStart}</span>
                      )}
                    </div>
                  ))}
                  {insights.observedPeople.length > 5 && (
                    <p className="text-xs text-slate-500 text-center">+{insights.observedPeople.length - 5} more</p>
                  )}
                </div>
              </InsightSection>
            )}

            {/* License Plates */}
            {insights.plates && insights.plates.length > 0 && (
              <InsightSection title={`License Plates (${insights.plates.length})`} icon={<Car className="h-4 w-4" />}>
                <div className="space-y-2">
                  {insights.plates.map((plate, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-700/50 rounded-lg px-3 py-2">
                      <span className="text-sm text-white font-mono">{plate.text}</span>
                      {plate.confidence && (
                        <span className="text-xs text-slate-400">{Math.round(plate.confidence * 100)}%</span>
                      )}
                    </div>
                  ))}
                </div>
              </InsightSection>
            )}

            {/* OCR Text */}
            {insights.ocr && insights.ocr.length > 0 && (
              <InsightSection title={`OCR Text (${insights.ocr.length})`} icon={<FileText className="h-4 w-4" />}>
                <div className="space-y-1">
                  {insights.ocr.slice(0, 8).map((item, i) => (
                    <p key={i} className="text-sm text-slate-300 bg-slate-700/50 rounded px-2 py-1">{item.text}</p>
                  ))}
                </div>
              </InsightSection>
            )}

            {/* Keywords */}
            {insights.keywords && insights.keywords.length > 0 && (
              <InsightSection title="Keywords" icon={<ChevronRight className="h-4 w-4" />}>
                <div className="flex flex-wrap gap-1">
                  {insights.keywords.map((kw, i) => (
                    <span key={i} className="text-xs bg-violet-600/30 text-violet-300 px-2 py-0.5 rounded">{kw}</span>
                  ))}
                </div>
              </InsightSection>
            )}

            {/* Weapons Alert */}
            {insights.visionEnrichment?.weapons && insights.visionEnrichment.weapons.length > 0 && (
              <InsightSection title={`⚠ Weapons Detected (${insights.visionEnrichment.weapons.length})`} icon={<AlertTriangle className="h-4 w-4" />}>
                <div className="space-y-2">
                  {insights.visionEnrichment.weapons.map((w, i) => (
                    <div key={i} className="flex items-center gap-2 bg-red-900/40 border border-red-700 rounded-lg px-3 py-2">
                      <AlertTriangle className="h-4 w-4 text-red-400 flex-shrink-0" />
                      <span className="text-sm text-red-200 font-medium capitalize">{w}</span>
                    </div>
                  ))}
                </div>
              </InsightSection>
            )}

            {/* Vehicles (Vision) */}
            {insights.visionEnrichment?.vehicles && insights.visionEnrichment.vehicles.length > 0 && (
              <InsightSection title={`Vehicles (${insights.visionEnrichment.vehicles.length})`} icon={<Car className="h-4 w-4" />}>
                <div className="space-y-2">
                  {insights.visionEnrichment.vehicles.map((v, i) => (
                    <div key={i} className="flex items-center justify-between bg-slate-700/50 rounded-lg px-3 py-2">
                      <div>
                        <span className="text-sm text-white capitalize">{v.color} {v.type}</span>
                        {v.details && <p className="text-xs text-slate-400">{v.details}</p>}
                      </div>
                      {v.licensePlate && (
                        <span className="text-xs font-mono bg-yellow-600/30 text-yellow-300 px-2 py-0.5 rounded border border-yellow-600/50">{v.licensePlate}</span>
                      )}
                    </div>
                  ))}
                </div>
              </InsightSection>
            )}

            {/* Detected Objects (VI) */}
            {insights.detectedObjects && insights.detectedObjects.length > 0 && (
              <InsightSection title={`Detected Objects (${insights.detectedObjects.length})`} icon={<Tag className="h-4 w-4" />}>
                <div className="space-y-2">
                  {(() => {
                    const counts: Record<string, number> = {};
                    insights.detectedObjects!.forEach(o => {
                      const key = o.displayName || o.type;
                      counts[key] = (counts[key] || 0) + 1;
                    });
                    return Object.entries(counts).map(([name, count]) => (
                      <div key={name} className="flex items-center justify-between bg-slate-700/50 rounded-lg px-3 py-2">
                        <span className="text-sm text-white capitalize">{name}</span>
                        <span className="text-xs text-violet-300 font-bold">×{count}</span>
                      </div>
                    ));
                  })()}
                </div>
              </InsightSection>
            )}

            {/* Scene Labels */}
            {insights.labels && insights.labels.length > 0 && (
              <InsightSection title="Scene Labels" icon={<Tag className="h-4 w-4" />}>
                <div className="flex flex-wrap gap-1">
                  {insights.labels.map((l, i) => (
                    <span key={i} className="text-xs bg-slate-600/50 text-slate-300 px-2 py-0.5 rounded border border-slate-600">{l}</span>
                  ))}
                </div>
              </InsightSection>
            )}

            {/* Vision Scene Description */}
            {insights.visionEnrichment?.sceneDescription && (
              <InsightSection title="Scene Analysis (GPT-4o Vision)" icon={<Eye className="h-4 w-4" />}>
                <p className="text-sm text-slate-300 whitespace-pre-wrap">
                  {insights.visionEnrichment.sceneDescription}
                </p>
                {insights.visionEnrichment.location?.details && (
                  <p className="text-xs text-slate-500 mt-2">
                    Location: {insights.visionEnrichment.location.type} — {insights.visionEnrichment.location.details} | Lighting: {insights.visionEnrichment.lighting}
                  </p>
                )}
              </InsightSection>
            )}

            {/* Vision People Descriptions */}
            {insights.visionEnrichment?.people && insights.visionEnrichment.people.length > 0 && !(insights.observedPeople && insights.observedPeople.length > 0) && (
              <InsightSection title={`People (Vision Analysis — ${insights.visionEnrichment.people.length})`} icon={<Users className="h-4 w-4" />}>
                <div className="space-y-2">
                  {insights.visionEnrichment.people.map((p, i) => (
                    <div key={i} className="bg-slate-700/50 rounded-lg px-3 py-2">
                      <p className="text-sm text-white">Person #{i + 1}: {p.description}</p>
                      <p className="text-xs text-slate-400">Clothing: {p.clothing} | Action: {p.action} | Position: {p.position}</p>
                    </div>
                  ))}
                </div>
              </InsightSection>
            )}

            {/* Transcript */}
            {insights.transcript && (
              <InsightSection title="Transcript" icon={<FileText className="h-4 w-4" />}>
                <p className="text-sm text-slate-300 whitespace-pre-wrap max-h-40 overflow-y-auto">
                  {insights.transcript.slice(0, 500)}{insights.transcript.length > 500 ? '...' : ''}
                </p>
              </InsightSection>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function InsightSection({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-violet-400 mb-2">
        {icon}
        <span className="text-xs font-semibold uppercase tracking-wider">{title}</span>
      </div>
      {children}
    </div>
  );
}
